import {
  type CharacterShape,
  type Stroke,
  type StrokePoints,
  characterShape,
} from "./stroke-shape.js";
import {
  cheapest,
  costsAgainst,
  leastCost,
  movedOnto,
} from "./stroke-step-costs.js";
import { unitSquare } from "./taught-strokes.js";

/**
 * Marking one stroke as it lands, against the stroke a drill wanted next.
 *
 * The recogniser and `stroke-assignment.ts` mark a whole character once it is
 * written. A 汉字 writing drill (#962) marks each stroke as the reader lifts
 * the pen, so that a stroke out of order, backwards or in the wrong place is
 * shown and drawn again before the next one. The question here is narrower
 * than a lookup's. The character is known, the strokes already drawn are known,
 * and what is asked is what the latest stroke was a go at.
 *
 * **Everything is described in the square, and the square is the character.**
 * A drill draws the character's outline in the writing square, or the place it
 * would be, so where a stroke lands in the square is where it lands in the
 * character. `characterShape` is therefore given the unit square as its frame,
 * and a lone 横 drawn across the top is described as sitting at the top. Scaled
 * to its own bounds, as a lookup scales the ink, it would fill the square
 * wherever it was drawn and say nothing about its place. `taught-strokes.ts`
 * puts the outline file's medians in the same square.
 *
 * **The cost is the lookup's own.** `strokeCost` asks which way each run goes,
 * how far along the stroke it reaches, where the stroke sits and how long it
 * is, and the drill asks nothing it does not. `stroke-step-costs.ts` bounds
 * where the ink may sit and moves it about. What is new here is the reading
 * of the answer, which {@link markStrokeStep} gives in five words.
 *
 * `docs/handwriting.md` under "Marking one stroke at a time" has the
 * measurements the thresholds come from.
 */

/** What became of the stroke the reader just drew. */
export type StrokeStepVerdict =
  /** The stroke wanted next, drawn well enough to take. */
  | { readonly kind: "right" }
  /** Another stroke of the character, drawn out of turn. Which one is named. */
  | { readonly kind: "order"; readonly stroke: number }
  /** The stroke wanted next, drawn from its end to its start. */
  | { readonly kind: "backwards" }
  /** The stroke wanted next by its shape, landed somewhere else. */
  | { readonly kind: "place" }
  /** None of the character's strokes. */
  | { readonly kind: "wrong" };

/** One stroke of a drill, as it lands. */
export interface StrokeStep {
  /**
   * The character's strokes in writing order, each in the unit square.
   *
   * `taughtStrokes` in `taught-strokes.ts` takes them off the outline file.
   */
  readonly taught: readonly StrokePoints[];

  /** Which of them is wanted next. */
  readonly next: number;

  /** The stroke the reader drew, in the same unit square. */
  readonly stroke: StrokePoints;
}

/**
 * The most a stroke may cost against the one wanted and still be taken for it.
 *
 * Measured over 300 characters, each stroke of each written three ways from
 * its own median, as `docs/handwriting.md` describes the hand. The figure is
 * where the right stroke is taken nearly always and another stroke of the same
 * character nearly never, and the table there has both rates either side of it.
 */
export const strokeStepCost = 0.4;

/**
 * How much nearer another stroke may be than the one wanted before it is
 * named instead.
 *
 * Twins. The two 竖 of 冊 and the three 横 of 王 quantise to the same
 * description or to descriptions a cell apart, and a reader tracing the second
 * of them lands between the two. A tie went to the lower index, which told a
 * reader drawing the stroke they were asked for that they had drawn its twin
 * out of turn. One cell of place costs 0.114, so a lean of a cell takes the
 * wanted stroke over any twin the grid cannot tell from it.
 */
export const strokeStepLean = 0.12;

/**
 * How much better the ink has to fit the wanted stroke reversed than as drawn
 * before it is called backwards.
 *
 * A 横折 drawn from its end goes up and then left, and each of its runs sits
 * about fifty degrees from the run it replaces, which costs 0.28 and would be
 * taken. Reversed, the same ink costs a few hundredths. The gap is what a
 * reader's own wobble leaves between the two readings of a stroke drawn the
 * right way, and {@link dotLength} covers the short end where it leaves none.
 */
export const backwardsGap = 0.1;

/**
 * The length below which a stroke is a 点 and has no direction worth marking.
 *
 * In 64ths of the square. A dot's direction comes out of a few pointer events
 * and lands anywhere, so a dot is costed the better of the two ways round and
 * is never called backwards.
 */
export const dotLength = 8;

/** One stroke described in the square, or nothing where nothing was drawn. */
function described(points: StrokePoints): Stroke | undefined {
  return characterShape([points], unitSquare)[0];
}

/**
 * The corner tolerance the character is described at a second time.
 *
 * Twice the measured one. A lead-in or a hook the outlines record as a run of
 * its own is smoothed into the stroke, and a real 折 stays two runs, its
 * corner being far further off the line than this. See `leastCost`.
 */
export const coarseCorner = 0.12;

/** Every stroke of the character described in the square, two ways. */
function taughtShapes(taught: readonly StrokePoints[]): {
  readonly fine: CharacterShape;
  readonly coarse: CharacterShape;
} {
  return {
    fine: characterShape(taught, unitSquare),
    coarse: characterShape(taught, unitSquare, coarseCorner),
  };
}

/**
 * Whether the ink was the wanted stroke drawn from its end.
 *
 * Never of a 点, whose cost is taken the better way round instead, which
 * writes over its entry in `costs`.
 */
function isBackwards(
  wanted: Stroke,
  costs: number[],
  turned: number,
  next: number,
  within: number,
): boolean {
  if (wanted.length < dotLength) {
    costs[next] = Math.min(costs[next] as number, turned);

    return false;
  }

  return turned <= within && turned < (costs[next] as number) - backwardsGap;
}

/**
 * Mark the stroke the reader just drew against the one the drill wanted.
 *
 * The reading goes in order of what a drill does with it. Ink that fits the
 * wanted stroke far better reversed than as drawn is backwards, since a 横折
 * drawn from its end goes up and then left and is a different correction from
 * a wrong stroke. The stroke wanted, close enough and no further than a twin of
 * it, is taken. Another stroke of the character, closer than the one wanted and
 * close enough, is named as the one drawn out of turn. Failing those, the ink
 * is moved onto the wanted stroke, since the right shape in the wrong place is
 * its own correction. Anything left is wrong.
 *
 * @param step - The character, the stroke wanted, and the stroke drawn.
 * @param within - The most a stroke may cost and be taken. The measured
 * {@link strokeStepCost} unless a measurement is asking.
 * @returns What the drawn stroke was a go at.
 */
export function markStrokeStep(
  step: StrokeStep,
  within = strokeStepCost,
): StrokeStepVerdict {
  const { taught, next } = step;
  const { fine, coarse } = taughtShapes(taught);
  const wanted = fine[next];
  const drawn = described(step.stroke);
  const reversed = described(step.stroke.toReversed());

  if (wanted === undefined || drawn === undefined || reversed === undefined) {
    return { kind: "wrong" };
  }

  const costs = costsAgainst(drawn, fine, coarse);
  const turned = leastCost(reversed, wanted, coarse[next]);

  if (isBackwards(wanted, costs, turned, next, within)) {
    return { kind: "backwards" };
  }

  const nearest = cheapest(costs);
  const nearestCost = costs[nearest] as number;
  const wantedCost = costs[next] as number;

  if (wantedCost <= within && wantedCost <= nearestCost + strokeStepLean) {
    return { kind: "right" };
  }

  if (nearestCost <= within) {
    return { kind: "order", stroke: nearest };
  }

  const moved = described(movedOnto(step.stroke, drawn, wanted));

  if (moved !== undefined && leastCost(moved, wanted, coarse[next]) <= within) {
    return { kind: "place" };
  }

  return { kind: "wrong" };
}
